import { Container } from "pixi.js";
import { Manager } from "../core/Manager";

import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { RespinIndex, setDoubleReelFeature, setMaxWinBtnClosedGlobal, waitUntilMaxWinBtnClosed, willBannerAutoProceedGlobal } from "../config/GlobalVariableConfig";
import { spinData } from "../server/getServerResposesFun";
import { BigWin } from "./BigWin";

import { sound } from "@pixi/sound";
import { Group } from "tweedle.js";
import { BackGroundComponent } from "../components/backGround/BackGroundComponent";
import { CharacterComponent } from "../components/character/CharacterComponent";
import { LogoComponent } from "../components/logo/LogoComponent";
import { HoldReelComponent } from "../components/reels/HoldReelComponent";
import { ReelFrameComponent } from "../components/reels/ReelFrameComponent";
import { WinSeqComponent } from "../components/TransCoins/WinSeqComponent";
import { MaxGameWidthHeight } from "../config/reelConfig";
import { DisplayComponent } from "../core/displays/DisplayComponent";
import { DisplayComponentFactory } from "../core/displays/DisplayComponentFactory";
import { holdAndSpinEmitter } from "../core/reelsHoldSpin/ReelInit";
import { wait } from "../core/utils";
import { FeatureIndicatorColor } from "./Banner";
import { BaseGameScene } from "./BaseGameScene";
import { HoldBaseScene } from "./HoldBaseScene";


const tweenGroup: Group = new Group();

declare const window: any;

export type PotType = "doubleReels" | "extraSpin" | "multipliers";

const landscapePositionsMap: Record<number, { x: number, y: number }[]> = {
    1: [{ x: 950, y: 100 }],
    2: [{ x: 820, y: 100 }, { x: 1080, y: 100 }],
    3: [{ x: 720, y: 100 }, { x: 950, y: 100 }, { x: 1200, y: 100 }]
};

const portraitPositionsMap: Record<number, { x: number, y: number }[]> = {
    1: [{ x: 950, y: 77 }],
    2: [{ x: 820, y: 77 }, { x: 1080, y: 77 }],
    3: [{ x: 720, y: 77 }, { x: 950, y: 77 }, { x: 1200, y: 77 }]
};

export class FreeGameScene extends HoldBaseScene {

    // Asset bundles
    override assetBundles: string[] = ["game-screen", "sound", "intro-screen", "big-win"];

    // Containers
    private potContainer = new Container();

    private numberOfFS: number;
    private baseGameWin: number;
    private totalFGSessionWin: number;

    private firstSpin: boolean = true;
    private numberOfFSPlayed: number = 0;
    private autoTriggerTimeout: ReturnType<typeof setTimeout>;

    private fgTransition: Spine | null = null;
    private transitionBeforeWin: Spine | null = null;


    private greenFeatureIndicator: Spine;   // poseidon
    private blueFeatureIndicator: Spine;    // zeus
    private redFeatureIndicator: Spine;     // hades
    private addedFeatureIndicators: Spine[] = [];

    private boundTransitionAnimation: () => void;
    private pointerDownHandlerFree: () => void;

    private isDoubleReelFeature: boolean;

    constructor(numberOfFS: number, baseGameWin: number) {
        super();
        this.numberOfFS = numberOfFS;
        this.numberOfFSPlayed = 0;
        this.baseGameWin = baseGameWin;
        this.totalFGSessionWin = baseGameWin;
        this.firstSpin = true;

        this.pointerDownHandlerFree = this.onBannerPointerDown.bind(this);
        this.boundTransitionAnimation = this.playTransitionBeforeWin.bind(this);

        holdAndSpinEmitter.on('transitionAnimation', this.boundTransitionAnimation);

        this.setupFeatureIndicators();
    }

    override destroy() {
        DisplayComponent.unregister(HoldReelComponent);
        DisplayComponent.unregister(WinSeqComponent);
        DisplayComponent.unregister(CharacterComponent);

        holdAndSpinEmitter.off('transitionAnimation', this.playTransitionBeforeWin);

        this.reels?.destroy(true);
        this.reels = null;
        super.destroy({ children: true, texture: false });
    }



    public async constructorWithAssets(): Promise<void> {

        this.isDoubleReelFeature = false;
        setDoubleReelFeature(this.isDoubleReelFeature);

        this.initBaseLayering();

        DisplayComponent.get(BackGroundComponent).getInstance().playAnimation("bonus_game", true);

        this.addReelComponent();
        this.addTransCoinsComponent();


        this.game.addChild(this.potContainer);

        this.setupReelFrame("reelBackSkel", "reelBackAtlas", false);
        DisplayComponent.get(ReelFrameComponent).playAnimation("bonus_game_reel", true);
        this.setupCommonUI();

        this.addComponent(CharacterComponent, this.game);

        const reels = DisplayComponent.get(HoldReelComponent).getInstance();
        reels.addCarryForwardCoins();

        this.bigWin = new BigWin(Manager.screenwidth, Manager.screenHeight);
        this.bigWin.position.set(Manager.screenwidth / 2, Manager.screenHeight / 2);
        this.addChild(this.bigWin);

        this.setupTransition();
        this.setupTransitionBeforeWin();

        this.resize(Manager.screenwidth, Manager.screenHeight);
    }


    private setupFeatureIndicators(): void {


        this.greenFeatureIndicator = Spine.from({
            skeleton: "oceanTriggerIndicatorSkel",
            atlas: "oceanTriggerIndicatorAtlas"
        });
        this.greenFeatureIndicator.scale = 0.9;
        this.greenFeatureIndicator.state.setAnimation(0, "loop", true);


        this.blueFeatureIndicator = Spine.from({
            skeleton: "lightTriggerIndicatorSkel",
            atlas: "lightTriggerIndicatorAtlas"
        });
        this.blueFeatureIndicator.scale = 0.9;
        this.blueFeatureIndicator.state.setAnimation(0, "loop", true);


        this.redFeatureIndicator = Spine.from({
            skeleton: "infernoTriggerIndicatorSkel",
            atlas: "infernoTriggerIndicatorAtlas"
        });
        this.redFeatureIndicator.scale = 0.9;
        this.redFeatureIndicator.state.setAnimation(0, "loop", true);

        this.potContainer.label = "potContainer";
    }


    private resetFeatureIndicatorContainers(): void {
        this.addedFeatureIndicators.forEach((indicator) => {
            if (indicator.parent) {
                indicator.parent.removeChild(indicator);
            }
        });
        this.addedFeatureIndicators = [];
    }


    private getCurrentPositionsMap(): Record<number, { x: number, y: number }[]> {
        return Manager.screenwidth > Manager.screenHeight
            ? landscapePositionsMap
            : portraitPositionsMap;
    }


    private addFeatureIndicators(indicatorColors: FeatureIndicatorColor[]): void {
        this.resetFeatureIndicatorContainers();

        const positions = this.getCurrentPositionsMap()[indicatorColors.length];

        indicatorColors.forEach((color, index) => {
            let indicator!: Spine;

            switch (color) {
                case "green":
                    indicator = this.greenFeatureIndicator;
                    break;
                case "blue":
                    indicator = this.blueFeatureIndicator;
                    break;
                case "red":
                    indicator = this.redFeatureIndicator;
                    break;
                default: {
                    const _exhaustive: never = color;
                    throw new Error(`Unhandled feature indicator color: ${_exhaustive}`);
                }
            }

            indicator.position.set(positions[index].x, positions[index].y);
            this.potContainer.addChild(indicator);
            this.addedFeatureIndicators.push(indicator);
        });
    }


    private getIndicatorColors(): FeatureIndicatorColor[] {
        return (spinData.triggerFeatures ?? [])
            .map((feature): FeatureIndicatorColor | undefined => {
                switch (feature) {
                    case "poseidon": return "green";
                    case "zeus": return "blue";
                    case "hades": return "red";
                    default: return undefined;
                }
            })
            .filter((v): v is FeatureIndicatorColor => Boolean(v));
    }


    private callExitFreeGameAuto() {
        this.autoTriggerTimeout = setTimeout(() => {
            clearTimeout(this.autoTriggerTimeout);
            console.log("calling freeGameSceneChnage exitFreeGame pointerdown auto");
            this.exitFreeGame();
        }, 10000);
    }

    private onBannerPointerDown(): void {
        clearTimeout(this.autoTriggerTimeout);
        console.log("calling freeGameSceneChnage exitFreeGame pointerdown");
        this.exitFreeGame();
        Manager.banner.off('pointerdown', this.pointerDownHandlerFree);
    }

    public async freeSpinPlay() {
        console.log("casinoChannel-featureRoundStarted");
        console.log("this.numberOfFS", this.numberOfFS);
        console.log("casinoChannel-updateFeatureCounter");

        const fsRevealIndexes = spinData.respins
            .map((respin, index) => (respin.type === "fs_reveal" ? index : -1))
            .filter(index => index !== -1);

        console.log("fsRevealIndexes", fsRevealIndexes);

        let freeGameTrigger = 1;
        let winAmount: number;
        let accumulatedsWinAmount: number;

        while (freeGameTrigger != -1 && freeGameTrigger != -2) {

            Manager.casinoChannelHandler.reportFeatureRoundStarted();

            if (this.firstSpin) {
                this.firstSpin = false;

                this.numberOfFS = spinData.freeGameTrigger = spinData.triggerFeatures.includes("zeus") ? 3 : 3;

                Manager.casinoChannelHandler.reportUpdateFeatureCounter(this.numberOfFS);
                Manager.casinoChannelHandler.reportUpdateFeatureTotalWin((this.baseGameWin * 100).toFixed(0));
                await new Promise(resolve => setTimeout(resolve, 500));

                if (this.transitionChangePromise) {
                    await wait(500);
                    console.log("'change' event awaited and received in freeSpinPlay");
                }
            }

            if (this.numberOfFS >= 0) {
                this.numberOfFSPlayed = this.numberOfFSPlayed + 1;
            }

            [freeGameTrigger, winAmount, accumulatedsWinAmount] = await this.spinReel();

            console.log("Freegame while loop for", RespinIndex.value, freeGameTrigger, "winAmount", winAmount, "accumulatedsWinAmount", accumulatedsWinAmount);

            if (winAmount > 0 && freeGameTrigger != -1) {
                const isMaxWin = (freeGameTrigger === -2) ? true : false;
                await this.bigWin.bigWinFunction(winAmount, isMaxWin);
            }

            Manager.casinoChannelHandler.reportFeatureRoundCompleted((winAmount * 100).toFixed(0));

            const featureWinCentsNum = Math.floor(winAmount * 100);
            console.log("reportUpdateFeatureTotalWin", featureWinCentsNum);

            Manager.casinoChannelHandler.reportUpdateFeatureTotalWin((accumulatedsWinAmount * 100).toFixed(0));

            if (winAmount > 0) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            if (freeGameTrigger == -1 || freeGameTrigger == -2) {
                this.totalFGSessionWin = accumulatedsWinAmount;
            }
        }

        console.log("Freegame while loop exit");

        if (Manager.banner) {

            if (freeGameTrigger === -2) {
                setMaxWinBtnClosedGlobal(false);
                Manager.casinoChannelHandler.reportMaxWin();
                await waitUntilMaxWinBtnClosed();
            }

            Manager.casinoChannelHandler.reportFeatureExit();
            await Manager.banner.appearFreeGameOutroBanner(
                this.totalFGSessionWin,
                this.numberOfFSPlayed,
                async () => {
                    const game = new BaseGameScene();
                    await Manager.changeScene(game, false);
                },
            );

            if (willBannerAutoProceedGlobal) {
                this.callExitFreeGameAuto();
            }

            console.log("appearFreeGameOutroBanner");

            Manager.banner.on('pointerdown', this.pointerDownHandlerFree);
        }
    }

    private async exitFreeGame() {
        console.log("casinoChannel-featureExit");
        await Manager.banner?.disappearBanner();
        await wait(500);
        Manager.casinoChannelHandler.reportGameRoundCompleted();
    }

    private async spinReel(): Promise<[number, number, number]> {
        let freeGameTrigger = 0;
        let winAmount = 0;
        let accumulatedsWinAmount = 0;

        this.addFeatureIndicators(this.getIndicatorColors());

        if (this.reels) {
            [freeGameTrigger, winAmount, accumulatedsWinAmount] = await this.reels.startPlay("FREEGAME");
            console.log("freeGameTrigger", freeGameTrigger);
            console.log("winAmount", winAmount);
        }

        return [freeGameTrigger, winAmount, accumulatedsWinAmount];
    }


    private addTransCoinsComponent(): void {
        DisplayComponent.register(new DisplayComponentFactory(WinSeqComponent));
        const transCoins = DisplayComponent.get(WinSeqComponent);
        transCoins.init();
    }

    private addReelComponent(): void {
        DisplayComponent.register(new DisplayComponentFactory(HoldReelComponent));
        DisplayComponent.get(HoldReelComponent).init();
        this.reels = DisplayComponent.get(HoldReelComponent).getInstance();
        this.reels.label = "AllReels";
        this.background.addChild(this.reels);
    }


    private transitionChangePromise: Promise<void> | null = null;
    private resolveTransitionChange: (() => void) | null = null;

    private setupTransition(): void {
        this.fgTransition = Spine.from({
            skeleton: "transitionSkel",
            atlas: "transitionAtlas"
        });
        this.fgTransition.label = "fgTransition";
        this.addChild(this.fgTransition);

        this.transitionChangePromise = new Promise<void>((resolve) => {
            this.resolveTransitionChange = resolve;
        });

        this.fgTransition.state.addListener({
            start: () => { console.log("fgTransition Started"); },
            event: (_entry, event) => {
                if (event.data.name === "change") {
                    console.log("fgTransition emitted 'change' event");
                    if (this.resolveTransitionChange) {
                        this.resolveTransitionChange();
                        this.resolveTransitionChange = null;
                    }
                }
            },
            complete: () => {
                this.fgTransition?.state.clearListeners();
                this.fgTransition?.destroy({ children: true, texture: false });
                this.fgTransition = null;
                console.log("fgTransition completed");
            }
        });
    }

    private setupTransitionBeforeWin(): void {
        this.transitionBeforeWin = Spine.from({
            skeleton: "bonusCollectTransitionSkel",
            atlas: "bonusCollectTransitionAtlas"
        });
        this.transitionBeforeWin.label = "transitionBeforeWin";
        this.addChild(this.transitionBeforeWin);
    }

    private playTransitionBeforeWin(resolve: () => void): void {
        console.log("inside playTransitionBeforeWin");
        sound.play('transition_before_win_collect', { volume: 0.5 });
        this.transitionBeforeWin.state.setAnimation(0, "animation", false).listener = {
            complete: () => {
                this.transitionBeforeWin.state.clearListeners();
                resolve();
            }
        };
    }


    public override update(): void {
        tweenGroup.update();
        this.reels?.update();
        this.bigWin?.update();
    }

    public override resize(screenWidth: number, screenHeight: number): void {
        super.resize(screenWidth, screenHeight);

        let scale: number;
        const isLandscape = screenWidth > screenHeight;
        const character = DisplayComponent.get(CharacterComponent).getInstance();

        if (isLandscape) {
            scale = Math.min(screenWidth / 1920, screenHeight / 1080);

            this.background.scale.set(scale);
            this.background.x = -((MaxGameWidthHeight * scale - screenWidth) / 2);
            this.background.y = -((MaxGameWidthHeight * scale - screenHeight) / 2);

            // Keep reelFrame transform identical to BaseGameScene
            this.reelFrame.scale.set(0.9);
            this.reelFrame.position.set(1024, 900);

            this.background.addChild(this.reelFrame);
            DisplayComponent.get(LogoComponent).resize(true);
            DisplayComponent.get(CharacterComponent).resize(true);
            this.background.addChild(character);

        } else {
            scale = Math.min(screenWidth / 1080, screenHeight / 1920);

            this.background.scale.set(scale);
            this.background.x = -((MaxGameWidthHeight * scale - screenWidth) / 2);
            this.background.y = -((MaxGameWidthHeight * scale - screenHeight) / 2);

            // Keep reelFrame transform identical to BaseGameScene
            this.reelFrame.scale.set(1);
            this.reelFrame.position.set(1024, 900);

            DisplayComponent.get(CharacterComponent).resize(false);
            this.background.addChild(character);
            this.background.addChild(this.reelFrame);
            DisplayComponent.get(LogoComponent).resize(false);
        }

        this.potContainer.position.set(78, 468);
        this.addFeatureIndicators(this.getIndicatorColors());

        if (this.fgTransition) {
            this.fgTransition.scale.set(scale);
            this.fgTransition.position.set(screenWidth / 2, screenHeight / 2);
        }

        if (this.transitionBeforeWin) {
            this.transitionBeforeWin.scale.set(scale);
            this.transitionBeforeWin.position.set(screenWidth / 2, screenHeight / 2);
        }

        this.bigWin?.scale.set(scale);
        this.bigWin?.position.set(screenWidth / 2, screenHeight / 2);
        this.bigWin?.resize(screenWidth, screenHeight);
    }
}